import React, { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PrimaryBtn } from 'components/UI/Buttons';
import CloseableAlert from 'components/UI/CloseableAlert';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import useQuery from 'hooks/useQuery';
import { usePasswordRecoveryMutation, useSignInUserMutation } from 'services/api/user.api';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { identifyPosthogUser } from 'utils/posthogUtils';
import validateEmail from 'utils/validations/validateEmail';

import { signInUser } from '../../redux/slices/user.slice';

const SignIn: FC = () => {
  const [signIn, { data, isLoading, isSuccess, error }] = useSignInUserMutation();
  const [
    passwordRecovery,
    { isLoading: isLoadingPasswordRecovery, data: dataPasswordRecovery, isError: isErrorPasswordRecovery },
  ] = usePasswordRecoveryMutation();

  const query = useQuery();
  const dispatch = useDispatch();
  const passwordReset = query.get('reset');

  const [noEmailError, setNoEmailError] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [invalidEmail, setInvalidEmail] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      return;
    }
    const recaptchaToken = await getReCaptchaToken();
    const signInBody = {
      recaptchaToken,
      email: emailInput,
      password: formData.get('password')?.toString() || '',
    };
    await signIn(signInBody);
  };

  const handlePasswordRecovery = () => {
    if (!emailInput) {
      setNoEmailError(true);
      return;
    }
    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      return;
    }
    passwordRecovery({ email: emailInput }).catch((err) => {
      // eslint-disable-next-line no-console
      console.log(err);
    });
  };

  const inputHelperText = () => {
    if (noEmailError) {
      return 'Por favor ingrese su email para recuperar su contraseña';
    }
    if (invalidEmail) {
      return 'Por favor ingrese un email válido';
    }
    return '';
  };

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(signInUser(data));
      identifyPosthogUser(data);
    }
  }, [data, isSuccess]);

  const errorMessage = error && 'data' in error ? String(error.data) : null;

  return (
    <>
      <LoadingOverlay visible={isLoading || isLoadingPasswordRecovery} />
      <Grid sx={{ height: '100vh' }} container component="main">
        <Grid item xs={12} component={Paper} elevation={6} square>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', lg: '60%' },
              mx: 'auto',
              height: '100vh',
              alignItems: 'center',
              pt: '60px',
            }}
          >
            {passwordReset && <CloseableAlert msg="Se ha cambiado tu contraseña exitosamente" severity="success" />}
            {errorMessage && <CloseableAlert msg={errorMessage || ''} severity="error" />}
            {isErrorPasswordRecovery && (
              <CloseableAlert msg="Hubo un error al recuperar la contraseña" severity="error" />
            )}
            {dataPasswordRecovery && <CloseableAlert msg={dataPasswordRecovery.message} severity="success" />}
            <img alt="Zeller" src="/logo.png" height="170" />
            <Typography variant="h4">Iniciar Sesión</Typography>
            <Box
              sx={{
                bgcolor: 'backgroundGrey.main',
                p: 2,
                borderRadius: '20px',
                mt: 2,
                border: '1px solid hsla(210,18%,87%,1)',
                width: { xs: '88%', lg: '420px' },
              }}
              component="form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                handleSubmit(e).catch((err) => {
                  // eslint-disable-next-line no-console
                  console.error(err);
                });
              }}
            >
              <TextField
                id="email"
                margin="normal"
                required
                fullWidth
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                sx={{ bgcolor: '#fff' }}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                }}
                helperText={inputHelperText()}
              />
              <TextField
                id="password"
                margin="normal"
                required
                fullWidth
                label="Contraseña"
                name="password"
                type="password"
                autoComplete="password"
                sx={{ bgcolor: '#fff' }}
              />
              <PrimaryBtn sx={{ mt: 2, py: 1, mb: 1 }} type="submit" fullWidth variant="contained">
                Iniciar sesión
              </PrimaryBtn>
              <Typography
                variant="body1"
                color="initial"
                sx={{
                  py: 2,
                  px: 1,
                  my: 1,
                  textAlign: 'center',
                  fontWeight: '400',
                  width: '100%',
                  cursor: 'pointer',
                }}
                onClick={handlePasswordRecovery}
              >
                Olvidé mi contraseña
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SignIn;
